/**
 * AN EVENT'S OWN COLOUR
 * =====================
 *
 * Woodside's teal is the platform's action colour, and for most events that is
 * right. Some events are their own thing: Flannel Fest has a poster with an
 * orange on it that every student recognises before they read a word, and a
 * page that answers it in teal is the church's colour arguing with the
 * ministry's.
 *
 * So one value per event replaces the accent, and only the accent. Three rules
 * hold it in check:
 *
 * TEXT IS NEVER THE ACCENT. Navy stays the ink and slate stays the quiet text.
 * A page whose body copy is tinted is a page that is harder to read for the
 * sake of a brand somebody already recognised from the artwork.
 *
 * SEMANTIC COLOURS ARE NOT THEIRS TO SET. Rose still means cancelled and amber
 * still means look at this. An event that made its warnings orange because its
 * poster is orange has lost a word from the page's vocabulary.
 *
 * THE COLOUR IS NEVER ALTERED. Every surface that is the accent is the value
 * staff chose — one red on the page, the one off the poster. Darkening the
 * buttons to suit a label is what put two reds on it once already.
 *
 * WHAT SITS ON IT IS CHOSEN, NOT ASSUMED. An event that names its own ink
 * gets it, contrast or no contrast: that pairing is the church's call and
 * usually comes straight off the artwork. Where an event names none, the ink
 * is picked to be readable — white when white clears 4.5:1, otherwise a
 * near-black mixed down from the colour itself, so a light brand colour keeps
 * its exact value and still carries a legible label.
 */

export interface AccentTokens {
  /**
   * The colour as chosen, used EVERYWHERE — marks, rings, buttons, the dock,
   * the initials on an avatar. One red on the page, and it is the one off the
   * poster. Nothing is darkened for contrast any more, because darkening the
   * surfaces that carry words is exactly what made half the page a different
   * red from the other half.
   */
  base: string;
  /**
   * What goes ON it instead. The event's own ink when that ink can be read on
   * it, white when white can, and otherwise a near-black drawn from the
   * colour itself — which is how a light brand colour keeps its exact value
   * and still carries a legible label.
   */
  onAccent: string;
  /** Hover and pressed: the same colour, a step down. */
  deep: string;
  /** The wash behind a confirmation or a chip. */
  soft: string;
}

/** #rgb or #rrggbb, and nothing else — this value ends up inside CSS. */
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isHexColor(value: string | null | undefined): value is string {
  return typeof value === 'string' && HEX.test(value.trim());
}

function toRgb(hex: string): [number, number, number] {
  const h = hex.trim().slice(1);
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

const toHex = (rgb: [number, number, number]) =>
  `#${rgb
    .map((n) =>
      Math.round(Math.min(255, Math.max(0, n)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;

/** WCAG relative luminance. */
function luminance([r, g, b]: [number, number, number]): number {
  const f = (n: number) => {
    const c = n / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/** WCAG contrast between two colours. */
function contrast(a: [number, number, number], b: [number, number, number]): number {
  // Two in, two out: the sort cannot change that.
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

const mix = (rgb: [number, number, number], with_: [number, number, number], amount: number) =>
  // Both are three-tuples, so every index of one exists in the other.
  rgb.map((n, i) => n + ((with_[i] as number) - n) * amount) as [number, number, number];

/**
 * One colour a given distance toward another, as hex.
 *
 * Exported for the site chrome, which needs a ground the event's colour has
 * been mixed INTO rather than a surface that is the colour — see lib/chrome.ts
 * and the note there about the footer.
 */
export function mixHex(from: string, to: string, amount: number): string {
  return toHex(mix(toRgb(from), toRgb(to), amount));
}

const BLACK: [number, number, number] = [0, 0, 0];
const WHITE: [number, number, number] = [255, 255, 255];

/**
 * The tokens an accent fills, from the one or two values staff chose.
 *
 * THE COLOUR IS NEVER CHANGED. What changes is what sits on it: the ink is
 * chosen to clear 4.5:1 against the colour as given, preferring the event's
 * own, then white, then a near-black mixed from the colour itself. Cream on
 * Flannel Fest's orange is 2.69:1 and cannot be read at label sizes however
 * it is dressed up, so the button wears the dark — and the hero, which is
 * type over artwork rather than over the accent, keeps the cream.
 */
export function accentTokens(hex: string, inkHex?: string | null): AccentTokens {
  const rgb = toRgb(hex);
  const readable = (ink: [number, number, number]) => contrast(ink, rgb) >= 4.5;

  /**
   * AN EVENT THAT NAMES ITS INK GETS ITS INK. Flannel Fest's cream on its own
   * orange is 2.69:1, under the 4.5:1 AA asks for at label sizes, and no size
   * or weight fixes that — large text still wants 3:1. It is the pairing off
   * the poster and it is the church's call, made knowingly; this note is here
   * so the next person does not "fix" it by darkening the button and putting
   * two reds back on the page.
   *
   * Where an event names no ink we choose one that can be read: white when it
   * clears, otherwise a near-black mixed down from the colour itself.
   */
  let onAccent = WHITE;
  if (isHexColor(inkHex)) onAccent = toRgb(inkHex);
  else if (!readable(WHITE)) {
    // Down from the colour itself rather than to flat black: a warm red's
    // shadow is a dark red, and that is what a poster would have done.
    let dark = rgb;
    for (let step = 0; step <= 100 && !readable(dark); step++) dark = mix(rgb, BLACK, step / 100);
    onAccent = dark;
  }

  return {
    base: toHex(rgb),
    onAccent: toHex(onAccent),
    deep: toHex(mix(rgb, BLACK, 0.14)),
    soft: toHex(mix(rgb, WHITE, 0.88)),
  };
}
